import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { 
  UserRoleDto, 
  RolePrivilegeDto,
  PrivilegeName
} from '../api/types/dtos';

interface AdminAuthorizationState {
  userRoles: UserRoleDto[];
  rolePrivileges: Record<string, RolePrivilegeDto[]>;
  activePrivileges: Set<string>;
  loading: boolean;
  error: Error | null;
  lastUpdated: Date | null;
}

interface AdminAuthorizationContextType extends AdminAuthorizationState {
  // Permission checking methods
  hasPrivilege: (privilege: PrivilegeName | string) => boolean;
  hasAnyPrivilege: (privileges: PrivilegeName[] | string[]) => boolean;
  hasAllPrivileges: (privileges: PrivilegeName[] | string[]) => boolean;
  hasRole: (roleName: string) => boolean;
  hasAnyRole: (roleNames: string[]) => boolean;
  
  // Authorization management
  refreshAuthorization: () => Promise<void>;
  clearAuthorization: () => void;
  
  // Utility methods
  getActivePrivileges: () => string[];
  getActiveRoles: () => string[];
  getAuthorizationDebugInfo: () => any;
}

const AdminAuthorizationContext = createContext<AdminAuthorizationContextType>({
  // Default state
  userRoles: [],
  rolePrivileges: {},
  activePrivileges: new Set(),
  loading: true,
  error: null,
  lastUpdated: null,
  
  // Default methods
  hasPrivilege: () => false,
  hasAnyPrivilege: () => false,
  hasAllPrivileges: () => false,
  hasRole: () => false,
  hasAnyRole: () => false,
  refreshAuthorization: async () => {},
  clearAuthorization: () => {},
  getActivePrivileges: () => [],
  getActiveRoles: () => [],
  getAuthorizationDebugInfo: () => ({}),
});

export const useAdminAuthorization = () => useContext(AdminAuthorizationContext);

interface AdminAuthorizationProviderProps {
  children: ReactNode;
}

export function AdminAuthorizationProvider({ children }: AdminAuthorizationProviderProps) {
  const { user, isAuthenticated, isSessionReady } = useAuth();
  const _orgData = useOrganization();
  
  const [state, setState] = useState<AdminAuthorizationState>({
    userRoles: [],
    rolePrivileges: {},
    activePrivileges: new Set(),
    loading: true,
    error: null,
    lastUpdated: null,
  });


  // Build active privileges set from role privileges
  const buildActivePrivileges = useCallback((rolePrivileges: Record<string, RolePrivilegeDto[]>) => {
    const privileges = new Set<string>();
    
    Object.values(rolePrivileges).forEach(privilegeList => {
      privilegeList.forEach(rolePrivilege => {
        if (rolePrivilege.isActive && rolePrivilege.privilege.isActive) {
          privileges.add(rolePrivilege.privilege.name);
        }
      });
    });
    
    return privileges;
  }, []);

  const fetchAdminAuthorization = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const { api } = await import('../api/client/axiosClient');
      const { ENDPOINTS } = await import('../api/endpoints');
      
      
      // 1. Get user roles from admin endpoint
      const response = await api.get(ENDPOINTS.admin.roles());
      const responseData = (response as any).data;
      
      
      const userRoles = Array.isArray(responseData) ? responseData : (responseData.data || []);
      
      const rolePrivileges: Record<string, any[]> = {};
      
      if (userRoles.length > 0) {
        
        await Promise.all(
          userRoles.map(async (userRole: any) => {
            try {
              
              // Get privileges for this specific role using the existing endpoint
              const privilegesResponse = await api.get(ENDPOINTS.rolePrivileges.ownDetailed(userRole.role.id));
              const privilegesData = (privilegesResponse as any).data;
              
              
              // The privileges endpoint returns the array directly, not wrapped in a data property
              const privileges = Array.isArray(privilegesData) ? privilegesData : (privilegesData.data || []);
              rolePrivileges[userRole.role.id] = privileges;
              
            } catch (error) {
              rolePrivileges[userRole.role.id] = [];
            }
          })
        );
        
      } else {
        console.warn('⚠️ No user roles found, skipping privilege fetching');
      }
      
      const activePrivileges = buildActivePrivileges(rolePrivileges);
      
      const newState = {
        userRoles,
        rolePrivileges,
        activePrivileges,
        loading: false,
        error: null,
        lastUpdated: new Date(),
      };
      
      setState(newState);
      
    } catch (error) {
      // Handle specific error cases
      let errorMessage = (error as Error).message;
      
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as any;
        if (axiosError.response?.status === 403) {
          errorMessage = 'Usuario no tiene permisos para acceder a información de roles y privilegios';
        } else if (axiosError.response?.status === 401) {
          errorMessage = 'Sesión expirada. Por favor, inicia sesión nuevamente';
        } else if (axiosError.response?.status === 404) {
          errorMessage = 'Endpoint de autorización no encontrado';
        }
      }
      
      const errorState = {
        loading: false,
        error: new Error(errorMessage),
        lastUpdated: new Date(),
      };
      
      setState(prev => ({ ...prev, ...errorState }));
    }
  }, [buildActivePrivileges, user?.id]);

  // Permission checking methods
  const hasPrivilege = useCallback((privilege: PrivilegeName | string): boolean => {
    return state.activePrivileges.has(privilege);
  }, [state.activePrivileges]);

  const hasAnyPrivilege = useCallback((privileges: PrivilegeName[] | string[]): boolean => {
    return privileges.some(privilege => state.activePrivileges.has(privilege));
  }, [state.activePrivileges]);

  const hasAllPrivileges = useCallback((privileges: PrivilegeName[] | string[]): boolean => {
    return privileges.every(privilege => state.activePrivileges.has(privilege));
  }, [state.activePrivileges]);

  const hasRole = useCallback((roleName: string): boolean => {
    return state.userRoles.some(userRole => 
      userRole.isActive && userRole.roleId === roleName
    );
  }, [state.userRoles]);

  const hasAnyRole = useCallback((roleNames: string[]): boolean => {
    return state.userRoles.some(userRole => 
      userRole.isActive && roleNames.includes(userRole.roleId)
    );
  }, [state.userRoles]);

  const refreshAuthorization = useCallback(async () => {
    await fetchAdminAuthorization();
  }, [fetchAdminAuthorization]);

  const clearAuthorization = useCallback(() => {
    setState({
      userRoles: [],
      rolePrivileges: {},
      activePrivileges: new Set(),
      loading: false,
      error: null,
      lastUpdated: null,
    });
  }, []);

  const getActivePrivileges = useCallback(() => {
    return Array.from(state.activePrivileges);
  }, [state.activePrivileges]);

  const getActiveRoles = useCallback(() => {
    return state.userRoles
      .filter(userRole => userRole.isActive)
      .map(userRole => userRole.roleId);
  }, [state.userRoles]);

  // Debug method to get authorization state info
  const getAuthorizationDebugInfo = useCallback(() => {
    return {
      userRoles: state.userRoles.map(r => ({
        roleId: r.roleId,
        isActive: r.isActive
      })),
      activePrivileges: Array.from(state.activePrivileges),
      loading: state.loading,
      lastUpdated: state.lastUpdated,
      error: state.error?.message,
    };
  }, [state]);

  // Effect to fetch authorization when user is authenticated
  useEffect(() => {
    if (isAuthenticated && isSessionReady) {
      fetchAdminAuthorization();
    } else if (!isAuthenticated) {
      clearAuthorization();
    }
  }, [isAuthenticated, isSessionReady, fetchAdminAuthorization, clearAuthorization]);

  const contextValue: AdminAuthorizationContextType = {
    ...state,
    hasPrivilege,
    hasAnyPrivilege,
    hasAllPrivileges,
    hasRole,
    hasAnyRole,
    refreshAuthorization,
    clearAuthorization,
    getActivePrivileges,
    getActiveRoles,
    getAuthorizationDebugInfo,
  };

  return (
    <AdminAuthorizationContext.Provider value={contextValue}>
      {children}
    </AdminAuthorizationContext.Provider>
  );
}
